import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import PreLoader from './extra/PreLoader';
import AgriInsuranceClaims from './AgriInsuranceClaims';
import { useSearchParams } from 'react-router-dom';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_BADGE = {
  pending_approval: 'bg-warning',
  info_requested: 'bg-info',
  approved: 'bg-primary',
  declined: 'bg-danger',
  submitted_to_underwriter: 'bg-secondary',
  policy_issued: 'bg-success',
};
const FUNDS_LABEL = {
  locked: 'Premium locked in your wallet',
  debited: 'Premium paid',
  released: 'Premium returned to your wallet',
};

const AgriInsurance = () => {
  const { http, user, logout } = AuthenticateService();

  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => (['applications', 'claims'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'apply')); // 'apply' | 'applications' | 'claims'
  const [claimPolicy, setClaimPolicy] = useState(null);

  // The sidebar links here with ?tab=claims
  useEffect(() => {
    const wanted = searchParams.get('tab');
    if (['apply', 'applications', 'claims'].includes(wanted)) setTab(wanted);
  }, [searchParams]);

  const reportClaim = (policyNumber) => {
    setClaimPolicy(policyNumber);
    setTab('claims');
  };

  return (
    <>
      {loading ? <PreLoader /> : null}
      <div style={{ display: loading ? 'none' : 'block' }}>
        <Sidebar />
        <Header user={user} />

        <div className="content">
          <h4 className="fs-1 mb-4">Agricultural Insurance</h4>

          <div className="btn-group mb-4" role="group">
            <button type="button" className={`btn ${tab === 'apply' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('apply')}>
              Apply
            </button>
            <button type="button" className={`btn ${tab === 'applications' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('applications')}>
              My Applications
            </button>
            <button type="button" className={`btn ${tab === 'claims' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('claims')}>
              Claims
            </button>
          </div>

          {/* Both tabs stay mounted (hidden with CSS) so switching back and forth keeps whatever was typed or loaded. */}
          <div style={{ display: tab === 'apply' ? 'block' : 'none' }}>
            <ApplyForm http={http} logout={logout} onReady={() => setLoading(false)} onSubmitted={() => setTab('applications')} />
          </div>
          <div style={{ display: tab === 'applications' ? 'block' : 'none' }}>
            <MyApplications http={http} logout={logout} onReady={() => setLoading(false)} active={tab === 'applications'} onReportClaim={reportClaim} />
          </div>
          <div style={{ display: tab === 'claims' ? 'block' : 'none' }}>
            <AgriInsuranceClaims http={http} logout={logout} active={tab === 'claims'} startPolicy={claimPolicy} onStarted={() => setClaimPolicy(null)} />
          </div>

          <Footer />
        </div>

        <ToastContainer />
      </div>
    </>
  );
};

/* ---------------------------------------------------------------------
 * Apply
 * ------------------------------------------------------------------- */

const ApplyForm = ({ http, logout, onReady, onSubmitted }) => {
  const [loadError, setLoadError] = useState(null);
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [providerId, setProviderId] = useState('');
  const [productId, setProductId] = useState('');
  const [account, setAccount] = useState('');
  const [value, setValue] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  const [quoting, setQuoting] = useState(false);
  const [premium, setPremium] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const quoteTimer = useRef(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const res = await http.get('/agri-insurance/providers');
      if (res.data.status === true) {
        const d = res.data.data || {};
        setProviders(d.providers || []);
        setAccounts(d.accounts || []);
        if ((d.accounts || []).length > 0) setAccount(d.accounts[0].virtual_accnum);
      } else {
        setLoadError(res.data.message || 'Could not load insurance providers');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
        return;
      }
      setLoadError('Could not load insurance providers, please try again');
    } finally {
      onReady();
    }
  };

  const provider = providers.find((p) => String(p.id) === String(providerId));
  const products = provider?.products || [];
  const product = products.find((p) => String(p.id) === String(productId));

  const declaredValue = () => parseFloat(String(value).replace(/,/g, '')) || 0;

  const scheduleQuote = () => {
    setPremium(null);
    setQuoteError(null);
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    if (!product || declaredValue() <= 0) return;
    quoteTimer.current = setTimeout(runQuote, 500);
  };

  const runQuote = async () => {
    if (!product || declaredValue() <= 0) return;
    setQuoting(true);
    try {
      const res = await http.post('/agri-insurance/quote', { product_id: product.id, declared_value: declaredValue() });
      if (res.data.status === true) {
        setPremium(parseFloat(res.data.data.premium));
        setQuoteError(null);
      } else {
        setPremium(null);
        setQuoteError(res.data.message);
      }
    } catch (error) {
      setPremium(null);
      setQuoteError(error?.response?.data?.message || 'Could not calculate the premium');
    } finally {
      setQuoting(false);
    }
  };

  const rangeHint = () => {
    if (!product) return null;
    const min = product.min_asset_value;
    const max = product.max_asset_value;
    if (min == null && max == null) return null;
    return `Allowed: ${min != null ? money(min) : 'any'} to ${max != null ? money(max) : 'any'}`;
  };

  const submit = async () => {
    if (!product) return toast.error('Choose a provider and class of cover.');
    if (declaredValue() <= 0) return toast.error('Enter the declared value.');
    if (!account) return toast.error('No wallet account found.');

    let currentPremium = premium;
    if (currentPremium == null) {
      await runQuote();
      currentPremium = premium;
      if (currentPremium == null) return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm application',
      html: `A premium of <b>${money(currentPremium)}</b> will be locked in your wallet while your application is reviewed.<br/><br/>If it is approved the premium is charged. If it is declined the money returns to your wallet.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Lock & Apply',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await http.post('/agri-insurance/apply', {
        product_id: product.id,
        declared_value: declaredValue(),
        account_id: account,
        farm_location: location.trim(),
        asset_description: description.trim(),
      });
      if (res.data.status === true) {
        toast.success(res.data.message || 'Application submitted.');
        setProviderId('');
        setProductId('');
        setPremium(null);
        setValue('');
        setLocation('');
        setDescription('');
        onSubmitted();
      } else {
        toast.error(res.data.message || 'Could not submit your application');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not submit your application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="text-center py-5">
        <p className="text-700">{loadError}</p>
        <button className="btn btn-primary" onClick={load}>Try again</button>
      </div>
    );
  }

  if (providers.length === 0) {
    return <div className="alert alert-info">No insurance providers are available right now.</div>;
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Insurance provider</label>
            <select
              className="form-select"
              value={providerId}
              onChange={(e) => {
                setProviderId(e.target.value);
                setProductId('');
                setPremium(null);
              }}
            >
              <option value="">Select a provider</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Class of insurance</label>
            <select
              className="form-select"
              value={productId}
              disabled={!provider}
              onChange={(e) => {
                setProductId(e.target.value);
                scheduleQuote();
              }}
            >
              <option value="">Select a class of cover</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.premium_rate}%)</option>)}
            </select>
            {product?.description && <div className="text-500 fs--1 mt-1">{product.description}</div>}
          </div>

          <div className="col-md-6">
            <div className="form-floating">
              <input
                className="form-control"
                id="declaredValue"
                type="number"
                min="0"
                placeholder="Declared Value"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  scheduleQuote();
                }}
              />
              <label htmlFor="declaredValue">Declared value of farm asset / produce (₦)</label>
            </div>
            {rangeHint() && <div className="text-500 fs--1 mt-1">{rangeHint()}</div>}
          </div>

          <div className="col-md-6">
            <div className="p-3 rounded bg-success bg-opacity-10 h-100">
              {quoting ? (
                <span>Calculating premium...</span>
              ) : quoteError ? (
                <span className="text-danger">{quoteError}</span>
              ) : premium != null ? (
                <>
                  <div className="text-500">Premium due</div>
                  <div className="fs-2 fw-bold">{money(premium)}</div>
                  <div className="text-500 fs--1">{product?.premium_rate}% of the declared value</div>
                </>
              ) : (
                <span className="text-500">Choose a class of cover and enter the value to see your premium.</span>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-floating">
              <input className="form-control" id="farmLocation" placeholder="Farm Location" value={location} onChange={(e) => setLocation(e.target.value)} />
              <label htmlFor="farmLocation">Farm location</label>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating">
              <textarea
                className="form-control"
                id="assetDescription"
                placeholder="Describe the asset"
                style={{ height: 58 }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <label htmlFor="assetDescription">Describe the asset or produce (crop, animals, size...)</label>
            </div>
          </div>

          {accounts.length > 1 && (
            <div className="col-md-6">
              <label className="form-label">Pay premium from</label>
              <select className="form-select" value={account} onChange={(e) => setAccount(e.target.value)}>
                {accounts.map((a) => <option key={a.id} value={a.virtual_accnum}>{a.virtual_accnum}</option>)}
              </select>
            </div>
          )}

          <div className="col-12">
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={submit}>
              {submitting ? (<><Spinner animation="border" size="sm" /> Submitting...</>) : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------
 * My Applications
 * ------------------------------------------------------------------- */

const MyApplications = ({ http, logout, onReady, active, onReportClaim }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const loadedOnce = useRef(false);

  useEffect(() => {
    if (active && !loadedOnce.current) {
      loadedOnce.current = true;
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/agri-insurance/applications');
      if (res.data.status === true) {
        setItems(res.data.data || []);
      } else {
        setError(res.data.message || 'Could not load your applications');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError('Could not load your applications, please try again');
    } finally {
      setLoading(false);
      onReady();
    }
  };

  const sendReply = async () => {
    if (!replyTarget || !replyMessage.trim()) return;
    setSending(true);
    try {
      const res = await http.post(`/agri-insurance/applications/${replyTarget.id}/respond`, { message: replyMessage.trim() });
      toast[res.data.status === true ? 'success' : 'error'](res.data.message || (res.data.status ? 'Sent' : 'Could not send your reply'));
      if (res.data.status === true) {
        setReplyTarget(null);
        setReplyMessage('');
        load();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not send your reply');
    } finally {
      setSending(false);
    }
  };

  if (!active) return null;
  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (error) {
    return (
      <div className="text-center py-5">
        <p className="text-700">{error}</p>
        <button className="btn btn-primary" onClick={load}>Try again</button>
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="alert alert-info">You have no insurance applications yet.</div>;
  }

  return (
    <>
      <div className="row g-3">
        {items.map((a) => (
          <div key={a.id} className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="fw-bold">{a.reference}</span>
                  <span className={`badge ${STATUS_BADGE[a.status] || 'bg-secondary'}`}>{a.status_label}</span>
                </div>
                <div className="mb-1">{a.provider} · {a.product}</div>
                <div className="text-500 fs--1 mb-1">Declared value {money(a.declared_value)}</div>
                <div className="text-500 fs--1 mb-1">Premium {money(a.premium_amount)} ({a.premium_rate}%)</div>
                <div className="text-500 fs--1 mb-2">{FUNDS_LABEL[a.funds_status] || ''}</div>

                {a.status === 'info_requested' && (
                  <div className="p-2 rounded bg-info bg-opacity-10 mb-2">
                    <div className="fw-bold fs--1 mb-1">We need more information</div>
                    <div className="fs--1 mb-2">{a.info_request_note}</div>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => { setReplyTarget(a); setReplyMessage(''); }}
                    >
                      Reply
                    </button>
                  </div>
                )}

                {a.status === 'declined' && a.decline_reason && (
                  <div className="text-danger fs--1">Reason: {a.decline_reason}</div>
                )}

                {a.status === 'policy_issued' && a.policy_sent && (
                  <div className="text-success fs--1">Your policy document has been emailed to you.</div>
                )}

                {a.claimable && (
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2 pt-2 border-top">
                    <span className="fs--1">Policy number: <b>{a.policy_number}</b></span>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => onReportClaim(a.policy_number)}>Report a claim</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal show={!!replyTarget} onHide={() => setReplyTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Your reply</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Type your answer"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReplyTarget(null)}>Cancel</Button>
          <Button variant="primary" disabled={sending || !replyMessage.trim()} onClick={sendReply}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Send'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AgriInsurance;
